import React from "react";
import PropTypes from "prop-types";
import { formatCurrencyAUD } from "utils/formatCurrency";

const num = (value) => Number.parseFloat(value) || 0;
const dateText = (value) => {
  if (!value) return "\u2014";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

export default function QuotationDocument({
  values,
  items,
  quotationID,
  organisation,
  quoteNumber,
  currency,
  customer,
  validity,
}) {
  return (
    <article
      className="quotation-preview-document"
      data-pdf-document-id={quotationID}
      data-pdf-ready={Boolean(values.quotationNumber)}
    >
      <header className="quotation-preview-document-header">
        <h2>{organisation}</h2>
        <div>
          <h3>Quotation</h3>
          <span>{quoteNumber}</span>
        </div>
      </header>
      <div className="quotation-preview-rule" />
      <section className="quotation-preview-details">
        <div>
          <span>Prepared for</span>
          <strong>{customer}</strong>
        </div>
        <div>
          <span>Quote date</span>
          <strong>{dateText(values.quotationDate || values.createdAt)}</strong>
        </div>
        <div>
          <span>Validity</span>
          <strong>{validity}</strong>
        </div>
        <div>
          <span>Currency</span>
          <strong>{currency}</strong>
        </div>
      </section>
      <section className="quotation-preview-items-section">
        <h4>Items &amp; pricing</h4>
        <p>
          {values.gstType === "No GST"
            ? "No GST applies to this quotation"
            : "Unit prices and line totals include GST"}
        </p>
        <div className="quotation-preview-table-wrap">
          <table className="quotation-preview-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit price</th>
                <th>GST</th>
                <th>Discount</th>
                <th>Line total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item._id || `${item.itemName}-${index}`}>
                  <td>
                    <strong>{item.itemName || "Item"}</strong>
                    {item.itemDescription && (
                      <span>{item.itemDescription}</span>
                    )}
                  </td>
                  <td>{item.itemQuantity || 1}</td>
                  <td>${formatCurrencyAUD(num(item.itemPrice))}</td>
                  <td>{values.gstType === "No GST" ? 0 : num(item.gst)}%</td>
                  <td>
                    {item.discountType && item.discountType !== "None"
                      ? `${item.discountValue || 0}${
                          item.discountType === "Percentage" ? "%" : ""
                        }`
                      : "None"}
                  </td>
                  <td>
                    $
                    {formatCurrencyAUD(
                      num(item.totalItemPrice) ||
                        num(item.itemPrice) * num(item.itemQuantity || 1)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="quotation-preview-bottom-grid">
        <div className="quotation-preview-copy">
          {values.paymentInstruction?.trim() && (
            <div>
              <strong>Scope / customer notes</strong>
              <p>{values.paymentInstruction}</p>
            </div>
          )}
          <div>
            <strong>Terms</strong>
            <p>
              {values.terms ||
                "Listed supply and installation only. Additional work requires approval. This quote is valid for 14 days from sending."}
            </p>
          </div>
        </div>
        <div className="quotation-preview-totals">
          <div>
            <span>Subtotal excluding GST</span>
            <strong>${formatCurrencyAUD(num(values.totalAmount))}</strong>
          </div>
          <div>
            <span>
              {values.gstType === "No GST"
                ? "Total item discounts"
                : "Total item discounts (GST inclusive)"}
            </span>
            <strong>
              ${formatCurrencyAUD(num(values.totalDiscountAmount))}
            </strong>
          </div>
          <div>
            <span>
              {values.gstType === "No GST" ? "No GST" : "GST 10% (included)"}
            </span>
            <strong>${formatCurrencyAUD(num(values.totalGSTAmount))}</strong>
          </div>
          <div className="quotation-preview-total">
            <span>
              {values.gstType === "No GST" ? "Total" : "Total including GST"}
            </span>
            <strong>
              ${formatCurrencyAUD(num(values.finalAmount))}
              <small>{currency}</small>
            </strong>
          </div>
          <p>
            {values.gstType === "No GST"
              ? "No GST applies to this total."
              : "GST is included in the total, not added again."}
          </p>
        </div>
      </section>
    </article>
  );
}

QuotationDocument.propTypes = {
  values: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  quotationID: PropTypes.string,
  organisation: PropTypes.string.isRequired,
  quoteNumber: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  customer: PropTypes.string.isRequired,
  validity: PropTypes.string.isRequired,
};
